/**
 * RN-8431: react-native-web's useElementLayout observes in useLayoutEffect.
 * Chrome can deliver the initial ResizeObserver notifications in that same
 * frame, and UIManager.measure then reads layout (getBoundingClientRect /
 * offset*) during the first React 19 commit. On desktop RNVJ that shows up as
 * a long scheduler task with forced layout / TBT.
 *
 * Same contract as react-native-web 0.21's hook, except observe() is deferred
 * to the next animation frame so the first commit can paint without measuring.
 *
 * Copyright (c) Nicolas Gallagher. MIT (react-native-web useElementLayout).
 */

import { useEffect, useLayoutEffect as useReactLayoutEffect } from "react";
// Deep import so this module does not cycle through react-native-web's barrel
// (View → useElementLayout → this file).
// @ts-expect-error react-native-web ships no types for this internal module
import UIManager from "react-native-web/dist/exports/UIManager";

const canUseDOM = typeof window !== "undefined" && window.document != null;
const useLayoutEffect = canUseDOM ? useReactLayoutEffect : useEffect;

const DOM_LAYOUT_HANDLER_NAME = "__reactLayoutHandler";

type LayoutEvent = {
  nativeEvent: {
    layout: {
      x: number;
      y: number;
      width: number;
      height: number;
      left: number;
      top: number;
    };
  };
  timeStamp: number;
};

type OnLayout = (event: LayoutEvent) => void;

type LayoutNode = Element & {
  [DOM_LAYOUT_HANDLER_NAME]?: OnLayout | null;
};

type MeasureFn = (
  node: Element,
  callback: (
    x: number,
    y: number,
    width: number,
    height: number,
    left: number,
    top: number,
  ) => void,
) => void;

const measure: MeasureFn = (UIManager as { measure: MeasureFn }).measure;

let resizeObserver: ResizeObserver | null = null;
let didWarn = !canUseDOM;

function getResizeObserver(): ResizeObserver | null {
  if (canUseDOM && typeof window.ResizeObserver !== "undefined") {
    if (resizeObserver == null) {
      resizeObserver = new window.ResizeObserver((entries) => {
        entries.forEach((entry) => {
          const node = entry.target as LayoutNode;
          const onLayout = node[DOM_LAYOUT_HANDLER_NAME];
          if (typeof onLayout === "function") {
            measure(node, (x, y, width, height, left, top) => {
              const event = {
                nativeEvent: {
                  layout: { x, y, width, height, left, top },
                },
                timeStamp: Date.now(),
              };
              Object.defineProperty(event.nativeEvent, "target", {
                enumerable: true,
                get: () => entry.target,
              });
              onLayout(event);
            });
          }
        });
      });
    }
  } else if (!didWarn) {
    if (process.env.NODE_ENV !== "production" && process.env.NODE_ENV !== "test") {
      console.warn(
        "onLayout relies on ResizeObserver which is not supported by your browser. " +
          "Please include a polyfill, e.g., https://github.com/que-etc/resize-observer-polyfill.",
      );
      didWarn = true;
    }
  }
  return resizeObserver;
}

export default function useElementLayout(
  ref: { current: LayoutNode | null },
  onLayout?: OnLayout | null,
): void {
  const observer = getResizeObserver();

  useLayoutEffect(() => {
    const node = ref.current;
    if (node != null) {
      node[DOM_LAYOUT_HANDLER_NAME] = onLayout;
    }
  }, [ref, onLayout]);

  // Observing is a separate effect so this does not re-run when onLayout changes.
  useLayoutEffect(() => {
    const node = ref.current;
    if (node == null || observer == null) {
      return;
    }

    let frame = 0;
    if (typeof node[DOM_LAYOUT_HANDLER_NAME] === "function") {
      frame = requestAnimationFrame(() => {
        observer.observe(node);
      });
    } else {
      observer.unobserve(node);
    }

    return () => {
      if (frame !== 0) {
        cancelAnimationFrame(frame);
      }
      observer.unobserve(node);
    };
  }, [ref, observer]);
}
